/**
 * Automation Actions Registry
 *
 * Actions define WHAT an automation does (e.g., send message, assign plan, add tag).
 * This maps the `customType` field to configuration and React components.
 *
 * Architecture:
 * - Actions: What the automation does (this file)
 * - Triggers: When it fires + their own UI (automationTriggers.ts)
 * - Filters: Who the automation applies to (AutomationFilters)
 *
 * Each action specifies:
 * - `triggers`: Which trigger IDs are available for this action
 * - `WhatSection`: Component for editing action-specific config
 * - `WhatSummary`: Component for readonly summary of action config
 * - Feature flags: showMedia, showFilters, showMessage
 */

import { FC } from 'react';

import {
  AssessmentWhatSection,
  AssessmentWhatSummary,
} from './actions/AssessmentAction';
import {
  BannerAlertWhatSection,
  BannerAlertWhatSummary,
} from './actions/BannerAlertAction';
import { CancelWhatSection, CancelWhatSummary } from './actions/CancelAction';
import {
  ClearBannerAlertWhatSection,
  ClearBannerAlertWhatSummary,
} from './actions/ClearBannerAlertAction';
import {
  ClearModalAlertWhatSection,
  ClearModalAlertWhatSummary,
} from './actions/ClearModalAlertAction';
import { CustomWhatSection, CustomWhatSummary } from './actions/CustomAction';
import {
  DowngradeWhatSection,
  DowngradeWhatSummary,
} from './actions/DowngradeAction';
import { GroupWhatSection, GroupWhatSummary } from './actions/GroupAction';
import {
  LifecycleWhatSection,
  LifecycleWhatSummary,
} from './actions/LifecycleAction';
import { MessageWhatSection, MessageWhatSummary } from './actions/MessageAction';
import {
  ModalAlertWhatSection,
  ModalAlertWhatSummary,
} from './actions/ModalAlertAction';
import { PackageWhatSection, PackageWhatSummary } from './actions/PackageAction';
import { PlanWhatSection, PlanWhatSummary } from './actions/PlanAction';
import { ProductWhatSection, ProductWhatSummary } from './actions/ProductAction';
import {
  RequestInfoWhatSection,
  RequestInfoWhatSummary,
} from './actions/RequestInfoAction';
import {
  ResetBillingWhatSection,
  ResetBillingWhatSummary,
} from './actions/ResetBillingAction';
import {
  ResourceWhatSection,
  ResourceWhatSummary,
} from './actions/ResourceAction';
import { SummaryWhatSection, SummaryWhatSummary } from './actions/SummaryAction';
import { TagWhatSection, TagWhatSummary } from './actions/TagAction';
import { TrainerWhatSection, TrainerWhatSummary } from './actions/TrainerAction';
import {
  UpcomingAppointmentWhatSection,
  UpcomingAppointmentWhatSummary,
  UpcomingAppointmentWhenSection,
  UpcomingAppointmentWhenSummary,
} from './actions/UpcomingAppointmentAction';
import { VideoWhatSection, VideoWhatSummary } from './actions/VideoAction';
import {
  VisitCloseOutWhatSection,
  VisitCloseOutWhatSummary,
} from './actions/VisitCloseOutAction';
import {
  VisitsLeftWhatSection,
  VisitsLeftWhatSummary,
  VisitsLeftWhenSection,
  VisitsLeftWhenSummary,
} from './actions/VisitsLeftAction';
import {
  WorkoutTodayWhatSection,
  WorkoutTodayWhatSummary,
  WorkoutTodayWhenSection,
  WorkoutTodayWhenSummary,
} from './actions/WorkoutTodayAction';
import { AutomationItem } from './types';

// =============================================================================
// Types
// =============================================================================

/**
 * Props passed to action WHAT section components
 */
export interface ActionWhatSectionProps {
  automation: AutomationItem;
  index: number;
}

/**
 * Configuration for an automation action type.
 * Simplified: triggers handle their own UI, actions just define WHAT happens.
 */
export interface AutomationActionConfig {
  /** Display label for this action (e.g., "Assign / Import a Plan") */
  label: string;

  /** Trigger IDs available for this action (from automationTriggers.ts) */
  triggers: string[];

  /** Whether this action supports media/delivery method selection */
  showMedia: boolean;

  /** Whether this action supports client filters */
  showFilters: boolean;

  /** Whether this action supports a message field */
  showMessage: boolean;

  /** Whether this action supports the "Send To" section (recipient selection) */
  showSendTo?: boolean;

  /** Placeholder text for the "Send To" section when sending to client */
  sendToPlaceholder?: string;

  /** Component that renders the WHAT section (action-specific config) */
  WhatSection: FC<ActionWhatSectionProps>;

  /** Component that renders a summary for collapsed view */
  WhatSummary: FC<{ automation: AutomationItem }>;

  /** Optional custom WHEN section (overrides the default timing + trigger UI) */
  WhenSection?: FC<ActionWhatSectionProps>;

  /** Optional custom WHEN summary (overrides the default timing + trigger summary) */
  WhenSummary?: FC<{ automation: AutomationItem }>;

  /** Whether to hide the hour-of-day selector for recurring automations */
  hideHourOfDay?: boolean;

  /** Optional static options for this action type */
  options?: Record<string, Array<{ value: string; label: string }>>;
}

// =============================================================================
// Static Options
// =============================================================================

export const PLAN_DAY_OPTIONS = [
  { value: '', label: 'Only Assign Plan' },
  { value: 'current', label: 'Current Day of the Week' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

// =============================================================================
// Trigger Lists Per Action (from v2)
// =============================================================================

/**
 * Plan action: Uses sellableTypes directly stored in reminderType
 * In v2, Plan has a sellableTypes dropdown (not optionTypes)
 * Includes 'any' and 'some' triggers for selecting specific items
 */
const PLAN_TRIGGERS = [
  'all', // "purchases anything"
  'any', // "purchased anything"
  'some', // "purchased specific [items]"
  'workout_plan', // "purchases plan(s)"
  'group', // "purchases group(s)"
  'product', // "purchases product(s)"
  'package', // "purchases package(s)"
  'client', // "purchases client training"
];

/**
 * Assessment action: Uses optionTypes stored in reminderType
 * Note: In v2, 'any'/'some' options result in storing 'all' or a specific
 * sellable type (workout_plan, group, etc.) in reminderType.
 * We include all possible values so existing data displays correctly.
 */
const ASSESSMENT_TRIGGERS = [
  // Primary options from v2 optionTypes
  'any',
  'some',
  'add-tag',
  'visit-closed',
  'plan-started',
  // Sellable types (stored when user picks any/some + a specific type)
  'all',
  'workout_plan',
  'group',
  'product',
  'package',
  'client',
];

/** Message action: most comprehensive trigger set */
const MESSAGE_TRIGGERS = [
  'client-signup',
  'failed-payment',
  'birthdate',
  'anniversary',
  'trial-end',
  'subscription-renewal',
  'package-expired',
  'assessment-completed',
  'visit-booked',
  'visit-completed',
  'visit-closed',
  'visit-no-showed',
  'visit-late-canceled',
  'subscription-downgrade',
  'workout-plan-completed',
  'workout-completed',
  'any',
  'some',
  'opened-app',
  'group_joined',
  'add-tag',
  'remove-tag',
  'low-stock-threshold',
  'calendar-plan-ending',
  'calendar-ending',
  'week-plan-ending',
];

/** Tag action triggers */
const TAG_TRIGGERS = [
  'any',
  'some',
  'assessment',
  'group_joined',
  'subscription-downgrade',
  'book-appointment',
  'visit-completed',
  'visit-closed',
  'visit-no-showed',
  'visit-late-canceled',
  'no-visit-closed',
  'workout-plan-completed',
];

/** Lifecycle action triggers */
const LIFECYCLE_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
  'assessment',
  'group_joined',
  'subscription-downgrade',
  'book-appointment',
  'visit-closed',
  'no-visit-closed',
  'workout-plan-completed',
];

/** Resource action triggers */
const RESOURCE_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
];

/** Video action triggers */
const VIDEO_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
];

/** Group action triggers */
const GROUP_TRIGGERS = [
  'any',
  'some',
  'start-plan',
  'book-appointment',
  'add-tag',
  'remove-tag',
  'subscription-downgrade',
];

/** Package action triggers */
const PACKAGE_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
];

/** Product action triggers */
const PRODUCT_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
];

/** Trainer action triggers */
const TRAINER_TRIGGERS = [
  'any',
  'some',
  'start-plan',
  'book-appointment',
  'add-tag',
  'remove-tag',
  'group_joined',
  'assessment-completed',
];

/** Visit Close Out - recurring schedule, no standard triggers */
const VISIT_CLOSE_OUT_TRIGGERS: string[] = [];

/** Custom - recurring message/report */
const CUSTOM_TRIGGERS: string[] = [];

/** Summary - recurring workout summary */
const SUMMARY_TRIGGERS: string[] = [];

/** Modal Alert triggers */
const MODAL_ALERT_TRIGGERS = [
  'any',
  'some',
  'opened-app',
  'workout-completed',
  'add-tag',
  'remove-tag',
  'plan-workouts-completed',
  'plan-workout-completed',
  'app-open-interval',
  'workout-plan-completed',
  'start-plan',
  'failed-payment',
  'group_joined',
];

/** Banner Alert triggers */
const BANNER_ALERT_TRIGGERS = [
  'any',
  'some',
  'opened-app',
  'workout-completed',
  'add-tag',
  'remove-tag',
  'assessment-completed',
  'plan-workouts-completed',
  'plan-workout-completed',
  'app-open-interval',
  'workout-plan-completed',
  'start-plan',
];

/** Downgrade triggers */
const DOWNGRADE_TRIGGERS = [
  'any',
  'some',
  'add-tag',
  'remove-tag',
];

/** Cancel triggers */
const CANCEL_TRIGGERS = [
  'cancel_subscription',
  'expired_package',
];

/** Reset Billing triggers */
const RESET_BILLING_TRIGGERS = [
  'any',
  'some',
  'book-appointment',
  'group_joined',
  'add-tag',
  'remove-tag',
];

/** Request Info - recurring schedule, no standard triggers */
const REQUEST_INFO_TRIGGERS: string[] = [];

/** Workout Today - recurring schedule, no standard triggers */
const WORKOUT_TODAY_TRIGGERS: string[] = [];

/** Upcoming Appointment - recurring schedule with timing-based trigger */
const UPCOMING_APPOINTMENT_TRIGGERS: string[] = [];

/** Visits Left - threshold-based trigger */
const VISITS_LEFT_TRIGGERS: string[] = [];

/** Clear Modal Alert triggers */
const CLEAR_MODAL_ALERT_TRIGGERS = [
  'successful-payment',
  'add-tag',
  'remove-tag',
];

/** Clear Banner Alert triggers */
const CLEAR_BANNER_ALERT_TRIGGERS = [
  'any',
  'some',
  'opened-app',
  'workout-completed',
  'add-tag',
  'remove-tag',
  'plan-workouts-completed',
  'plan-workout-completed',
  'app-open-interval',
  'workout-plan-completed',
  'start-plan',
];

// =============================================================================
// Action Registry
// =============================================================================

export const AUTOMATION_ACTIONS: Record<string, AutomationActionConfig> = {
  plan: {
    label: 'Assign / Import a Plan',
    triggers: PLAN_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: PlanWhatSection,
    WhatSummary: PlanWhatSummary,
    options: { planDays: PLAN_DAY_OPTIONS },
  },
  assessment: {
    label: 'Assign an Assessment',
    triggers: ASSESSMENT_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: true,
    showSendTo: true,
    sendToPlaceholder: 'All Accounts',
    WhatSection: AssessmentWhatSection,
    WhatSummary: AssessmentWhatSummary,
  },
  message: {
    label: 'Send a Message',
    triggers: MESSAGE_TRIGGERS,
    showMedia: true,
    showFilters: true,
    showMessage: true,
    showSendTo: true,
    WhatSection: MessageWhatSection,
    WhatSummary: MessageWhatSummary,
  },
  'client-tag': {
    label: 'Add/Remove Tag',
    triggers: TAG_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: TagWhatSection,
    WhatSummary: TagWhatSummary,
  },
  lifecycle: {
    label: 'Assign a Lifecycle Stage',
    triggers: LIFECYCLE_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: LifecycleWhatSection,
    WhatSummary: LifecycleWhatSummary,
  },
  resource: {
    label: 'Assign a Resource',
    triggers: RESOURCE_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: true,
    WhatSection: ResourceWhatSection,
    WhatSummary: ResourceWhatSummary,
  },
  'close-out-visit': {
    label: 'Close Out Visit',
    triggers: VISIT_CLOSE_OUT_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    hideHourOfDay: true,
    WhatSection: VisitCloseOutWhatSection,
    WhatSummary: VisitCloseOutWhatSummary,
  },
  video: {
    label: 'Assign a Video',
    triggers: VIDEO_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: true,
    WhatSection: VideoWhatSection,
    WhatSummary: VideoWhatSummary,
  },
  'add-group': {
    label: 'Add to / Remove from Group',
    triggers: GROUP_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: GroupWhatSection,
    WhatSummary: GroupWhatSummary,
  },
  package: {
    label: 'Assign a Package',
    triggers: PACKAGE_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: true,
    WhatSection: PackageWhatSection,
    WhatSummary: PackageWhatSummary,
  },
  product: {
    label: 'Assign a Product',
    triggers: PRODUCT_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: ProductWhatSection,
    WhatSummary: ProductWhatSummary,
  },
  'assign-trainer': {
    label: 'Assign a Trainer',
    triggers: TRAINER_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: TrainerWhatSection,
    WhatSummary: TrainerWhatSummary,
  },
  custom: {
    label: 'Send a Message or Report: Recurring',
    triggers: CUSTOM_TRIGGERS,
    showMedia: true,
    showFilters: true,
    showMessage: true,
    showSendTo: true,
    WhatSection: CustomWhatSection,
    WhatSummary: CustomWhatSummary,
  },
  summary: {
    label: 'Send Workout Summary',
    triggers: SUMMARY_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: SummaryWhatSection,
    WhatSummary: SummaryWhatSummary,
  },
  'modal-alert': {
    label: 'Schedule an App Alert',
    triggers: MODAL_ALERT_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: ModalAlertWhatSection,
    WhatSummary: ModalAlertWhatSummary,
  },
  'banner-alert': {
    label: 'Assign an App Banner Alert',
    triggers: BANNER_ALERT_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: BannerAlertWhatSection,
    WhatSummary: BannerAlertWhatSummary,
  },
  'downgrade-user': {
    label: 'Downgrade User',
    triggers: DOWNGRADE_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: DowngradeWhatSection,
    WhatSummary: DowngradeWhatSummary,
  },
  'cancel-user': {
    label: 'Cancel User Client',
    triggers: CANCEL_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: CancelWhatSection,
    WhatSummary: CancelWhatSummary,
  },
  'reset-billing-cycle': {
    label: 'Reset Subscription Billing Cycle Anchor',
    triggers: RESET_BILLING_TRIGGERS,
    showMedia: false,
    showFilters: false,
    showMessage: false,
    WhatSection: ResetBillingWhatSection,
    WhatSummary: ResetBillingWhatSummary,
  },
  // New actions added for v2 compatibility
  all: {
    label: 'Request Info from Client',
    triggers: REQUEST_INFO_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: RequestInfoWhatSection,
    WhatSummary: RequestInfoWhatSummary,
  },
  'workout-today': {
    label: 'Send Upcoming Workout Reminder',
    triggers: WORKOUT_TODAY_TRIGGERS,
    showMedia: true,
    showFilters: true,
    showMessage: true,
    WhatSection: WorkoutTodayWhatSection,
    WhatSummary: WorkoutTodayWhatSummary,
    WhenSection: WorkoutTodayWhenSection,
    WhenSummary: WorkoutTodayWhenSummary,
  },
  'upcoming-appointment': {
    label: 'Send Upcoming Appointment Reminder',
    triggers: UPCOMING_APPOINTMENT_TRIGGERS,
    showMedia: true,
    showFilters: true,
    showMessage: true,
    showSendTo: true,
    WhatSection: UpcomingAppointmentWhatSection,
    WhatSummary: UpcomingAppointmentWhatSummary,
    WhenSection: UpcomingAppointmentWhenSection,
    WhenSummary: UpcomingAppointmentWhenSummary,
  },
  'visits-left': {
    label: 'Send Visits Left Reminder',
    triggers: VISITS_LEFT_TRIGGERS,
    showMedia: true,
    showFilters: true,
    showMessage: true,
    showSendTo: true,
    WhatSection: VisitsLeftWhatSection,
    WhatSummary: VisitsLeftWhatSummary,
    WhenSection: VisitsLeftWhenSection,
    WhenSummary: VisitsLeftWhenSummary,
  },
  'clear-modal-alert': {
    label: 'Clear App Alert',
    triggers: CLEAR_MODAL_ALERT_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: ClearModalAlertWhatSection,
    WhatSummary: ClearModalAlertWhatSummary,
  },
  'clear-banner-alert': {
    label: 'Clear App Banner Alert',
    triggers: CLEAR_BANNER_ALERT_TRIGGERS,
    showMedia: false,
    showFilters: true,
    showMessage: false,
    WhatSection: ClearBannerAlertWhatSection,
    WhatSummary: ClearBannerAlertWhatSummary,
  },
};

// Export trigger lists for reference
export {
  PLAN_TRIGGERS,
  ASSESSMENT_TRIGGERS,
  MESSAGE_TRIGGERS,
  TAG_TRIGGERS,
  LIFECYCLE_TRIGGERS,
  RESOURCE_TRIGGERS,
  VIDEO_TRIGGERS,
  GROUP_TRIGGERS,
  PACKAGE_TRIGGERS,
  PRODUCT_TRIGGERS,
  TRAINER_TRIGGERS,
  VISIT_CLOSE_OUT_TRIGGERS,
  CUSTOM_TRIGGERS,
  SUMMARY_TRIGGERS,
  MODAL_ALERT_TRIGGERS,
  BANNER_ALERT_TRIGGERS,
  DOWNGRADE_TRIGGERS,
  CANCEL_TRIGGERS,
  RESET_BILLING_TRIGGERS,
  REQUEST_INFO_TRIGGERS,
  WORKOUT_TODAY_TRIGGERS,
  UPCOMING_APPOINTMENT_TRIGGERS,
  VISITS_LEFT_TRIGGERS,
  CLEAR_MODAL_ALERT_TRIGGERS,
  CLEAR_BANNER_ALERT_TRIGGERS,
};

// =============================================================================
// Utility Functions
// =============================================================================

/** Get action config by customType */
export const getAutomationAction = (
  customType: string | null | undefined
): AutomationActionConfig | null => {
  if (!customType) return null;
  return AUTOMATION_ACTIONS[customType] || null;
};

/** Get all available actions for creation dropdown */
export const getAvailableActions = (): Array<{
  value: string;
  label: string;
}> => {
  return Object.entries(AUTOMATION_ACTIONS).map(([key, config]) => ({
    value: key,
    label: config.label,
  }));
};

// =============================================================================
// Backwards Compatibility (deprecated)
// =============================================================================

/** @deprecated Use AutomationActionConfig */
export type AutomationTypeConfig = AutomationActionConfig;
/** @deprecated Use AUTOMATION_ACTIONS */
export const AUTOMATION_TYPES = AUTOMATION_ACTIONS;
/** @deprecated Use getAutomationAction */
export const getAutomationType = getAutomationAction;
/** @deprecated Use getAvailableActions */
export const getAvailableAutomationTypes = getAvailableActions;
